import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useFeathers, useFormInputs, isPermitted, coveragePlanSpecifications, researcherLevelPermissions, loadPaginatedFrontend } from '../../app/util';
import {
  DataField,
  FieldInlineEdit,
  ManualVoteTally,
  CandidateProgressBar,
  Researcher,
  CFSelectInput,
  ElectionResultsSelectInput,
  LoadingSpinner,
  TaskPreviewTile,
  UpdateCandidateRace,
  CirclePhoto,
} from '../index';
import { ReferenceEditor } from '../ReferenceEditor';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { ReactComponent as WarningIcon } from '../../images/yellow-warning.svg';
import { ReactComponent as GreenCheck } from '../../images/green-check.svg';
import { Button, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from 'styled-components';
import ClockIcon from '@mui/icons-material/AccessTime';
import { Skeleton } from '@mui/material';
import {
  Link,
  useParams,
  useHistory
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { DesktopDatePicker as DatePicker } from '@mui/x-date-pickers';
import ProfilerEvaluationTile from '../ProfilerEvaluationTile';

const permissionsOrdered = ['researcher', 'reviewer', 'publisher', 'editor', 'admin', 'super-admin'];
const priorityPartyKeys = ['D', 'R', 'N', 'I'];

const sortPartyOptions = (partyA, partyB) => {
  const partyAPriority = priorityPartyKeys.indexOf(partyA.key);
  const partyBPriority = priorityPartyKeys.indexOf(partyB.key);
  const partyAOrder = partyAPriority === -1 ? priorityPartyKeys.length : partyAPriority;
  const partyBOrder = partyBPriority === -1 ? priorityPartyKeys.length : partyBPriority;

  if (partyAOrder !== partyBOrder) return partyAOrder - partyBOrder;

  return (partyA.name || partyA.key).localeCompare(partyB.name || partyB.key, undefined, { sensitivity: 'base' });
};

const defaultContact = {
  method: 'email',
  value: '',
  visibility: 'public',
};

const contactMethodOptions = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'form', label: 'Form' },
];

const getContactMethodLabel = (method) => {
  return contactMethodOptions.find(option => option.value === method)?.label || method;
};

const ProfilerEvaluationsSection = ({ candidate, feathers }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvaluations = async () => {
      if (!candidate?.profilerEvaluations?.length) {
        setLoading(false);
        return;
      }

      try {
        const evaluationIds = candidate.profilerEvaluations.map(e => e.evaluation);
        const res = await feathers.getService('profiler-evaluations').find({
          query: {
            _id: { $in: evaluationIds },
            $sort: { updatedAt: -1 }
          }
        });
        setEvaluations(res.data);
      } catch (err) {
        console.error('Error loading profiler evaluations:', err);
      } finally {
        setLoading(false);
      }
    };

    if (feathers && candidate) {
      loadEvaluations();
    }
  }, [feathers, candidate]);

  if (loading) {
    return <Skeleton variant="rectangular" height={100} />;
  }

  if (evaluations.length === 0) {
    return null;
  }

  return (
    <Section>
      <Typography variant='h3'>Profiler evaluations</Typography>
      <div className='seperator' />
      {evaluations.map((evaluation) => (
        <ProfilerEvaluationTile key={evaluation._id} evaluation={evaluation} />
      ))}
    </Section>
  );
};

const CandidateDataFieldEditor = ({
  candidateId,
  onError,
  scrapedContent,
  race,
  onAddRunningMate,
  refreshToken,
}) => {
  const feathers = useFeathers();

  const [ candidate, setCandidate ] = useState(null);
  const { stagingDraft = {}, qualified, coveragePlan, withdrawn, workflow } = (candidate || {});
  let qualStatus;
  if(qualified === 'yes') qualStatus = 'Qualified';
  else if(qualified === 'no') qualStatus = `Didn't qualify`;
  else qualStatus = 'To be determined';

  const user = useSelector(state => state.user);

  const editPermission = isPermitted(user, ['super-admin', 'editor', 'admin', 'researcher', 'reviewer', 'publisher']);
  const reviewerPermission = isPermitted(user, ['reviewer'])
  const editorPermission = isPermitted(user, ['super-admin', 'editor'])
  const researcherPermission = researcherLevelPermissions[user?.researcherLevel].includes(candidate?.priorityLevel);
  const publishPermission = coveragePlan ? isPermitted(user, permissionsOrdered.slice(permissionsOrdered.indexOf(coveragePlanSpecifications[coveragePlan].minPermissionToPublish))) : false;
  
  const {
    progress,
    synced
  } = stagingDraft;

  const [ saving, setSaving ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const [ editingLastChecked, setEditingLastChecked ] = useState(false);
  const [ missingInfoChecked, setMissingInfoChecked ] = useState(null);
  const [ savingLastChecked, setSavingLastChecked ] = useState(false);
  const [ tasks, setTasks ] = useState(null);
  const [ runningMateCandidate, setRunningMateCandidate ] = useState(null);
  const [ loadingRunningMate, setLoadingRunningMate ] = useState(false);
  const onErrorRef = useRef(onError);

  const [ qualEdits, onChangeQualEdits ]  = useFormInputs({ qualified });

  const [ editingRace, setEditingRace ] = useState(false);
  const [ editingFinance, setEditingFinance ] = useState(false);
  const [ newFinanceInput, setNewFinanceInput ] = useState(null);
  const [ savingFinance, setSavingFinance ] = useState(false);
  const [ refreshingFinanceData, setRefreshingFinanceData ] = useState(false);
  const [ newClarityInput, setNewClarityInput ] = useState(null);
  const [ savingElectionResults, setSavingElectionResults ] = useState(false);
  const [ editingElectionResults, setEditingElectionResults ] = useState(false);
  const [ refreshingElectionResults, setRefreshingElectionResults ] = useState(false);
  const [ editingQualified, setEditingQualified ] = useState(false);
  const [ editingWithdrawn, setEditingWithdrawn ] = useState(false);
  const [withdrawnStatus, setWithdrawnStatus] = useState(false);
  const [ editingParty, setEditingParty ] = useState(false);
  const [ partyInput, setPartyInput ] = useState('');
  const [ partyOptions, setPartyOptions ] = useState([]);
  const [ savingParty, setSavingParty ] = useState(false);
  const [ loadingParties, setLoadingParties ] = useState(false);
  const [ editingContact, setEditingContact ] = useState(false);
  const [ contactEdits, setContactEdits ] = useState([]);
  const [ savingContact, setSavingContact ] = useState(false);
  
  const election = useParams().key;
  const electionData = useSelector(state => state?.elections?.byKey[election])
  const history = useHistory();

  const editsMadeBy = useMemo(() => {
    const drafts = candidate?.previousDrafts || [];
    const usersUnique = [ stagingDraft, ...drafts ].map(d => d?.user || d?.systemUpdateSource).filter(Boolean).reduce((acc, cur) => {
      acc[cur?._id || cur] = cur;
      return acc;
    }, {});
    return Object.values(usersUnique).map(u => u === 'profiler' ? 'Profiler' : u?.name || u?.firstName || u?.email);
  }, [candidate, stagingDraft])

  useEffect(() => {
    if(editingWithdrawn) setWithdrawnStatus(withdrawn)
  }, [ editingWithdrawn ])

  useEffect(() => {
    if(editingParty) setPartyInput(candidate?.party || '')
  }, [ editingParty, candidate?.party ])

  useEffect(() => {
    if(!feathers || !editingParty || partyOptions.length > 0) return;

    let cancelled = false;

    const loadPartyOptions = async () => {
      setLoadingParties(true);
      try {
        const parties = await loadPaginatedFrontend('parties', { $sort: { key: 1 } }, feathers, 100);
        if(!cancelled) setPartyOptions((parties || []).sort(sortPartyOptions));
      } catch(err) {
        console.log(err);
        if(!cancelled) onErrorRef.current(err);
      } finally {
        if(!cancelled) setLoadingParties(false);
      }
    };

    loadPartyOptions();

    return () => {
      cancelled = true;
    };
  }, [ feathers, editingParty, partyOptions.length ])

  useEffect(() => {
    if(editingContact) setContactEdits(candidate?.contact || [])
  }, [ editingContact, candidate?.contact ])

  const loadCandidateData = async () => {
    if(loading) return;

    setLoading(true);
    try {
      const res = await feathers.getService('candidates').get(candidateId);
      setCandidate(res);
    } catch(err) {
      console.log(err)
      onError(err)
    } finally {
      setLoading(false)
    }
  }

  const loadTasks = async () => {
    try {
      const res = await feathers.getService('research-tasks').find({
        query: {
          'details.candidate': candidateId,
          $includeCheckedOut: true,
          $limit: 20,
          $sort: { createdAt: -1 }
        }
      });
      setTasks(res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch(err) {
      console.log('Error in loading tasks', err)
      setTasks([])
    }
  }

  useEffect(() => {
    if(feathers) {
      loadCandidateData()
      loadTasks()
    }
  }, [ feathers, candidateId, refreshToken ])

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const runningMate = candidate?.runningMate?.candidate;
    const runningMateId = runningMate?._id || runningMate;

    if(!feathers || !runningMateId) {
      setRunningMateCandidate(null);
      setLoadingRunningMate(false);
      return;
    }

    let cancelled = false;
    setLoadingRunningMate(true);

    feathers.getService('candidates').get(runningMateId)
      .then((res) => {
        if(!cancelled) setRunningMateCandidate(res);
      })
      .catch((err) => {
        if(!cancelled) {
          setRunningMateCandidate(null);
          onErrorRef.current(err);
        }
      })
      .finally(() => {
        if(!cancelled) setLoadingRunningMate(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ feathers, candidate?.runningMate?.candidate ])


  const formattedWorkflowStatus = useMemo(() => {
    // toolTip, title, icon

    // support old candidates:
    if (!workflow?.status) {
      if(synced) {
        return {
          toolTip: 'All changes have been published',
          title: 'Published',
          icon: <GreenCheck />
        }
      } else {
        return {
          toolTip: 'Some unpublished changes',
          title: 'Draft',
          icon: <WarningIcon />
        }
      }
    }
    if (workflow?.status === 'published') {
      return {
        toolTip: 'All changes have been published',
        title: 'Published',
        icon: <GreenCheck />
      }
    }

    if(workflow?.status === 'references-added') {
      const hasInfoToScrape = workflow?.scrapingDetails?.infoToScrape;
      if(hasInfoToScrape) {
        return {
          toolTip: 'Candidate has some info that can be scraped and is awaiting profiling',
          title: 'Awaiting profiler',
          icon: <ClockIcon />
        }
      } else {
        return {
          toolTip: 'Candidate is awaiting manual research',
          title: 'Awaiting research',
          icon: <ClockIcon />
        }
      }
    }

    // provide descriptions that are dependent on the users' role && workflow status
    if(publishPermission) {
      // offer descriptive insight into where we're at in the process
      if (workflow.status === 'draft' && !workflow.rejectionStatus) {
        return {
          toolTip: 'Candidate is currently in draft.',
          title: 'Draft',
          icon: <ClockIcon />
        }
      }
      if (['draft','researcher-submitted','reviewer-submitted'].includes(workflow.status) && workflow.rejectionStatus) {
        return {
          toolTip: 'Candidate has been rejected by publisher<br/>and awaits edits by ' +
            (workflow.status === 'reviewer-submitted' ? 'the reviewer' : 'the original researcher'),
          title: 'Needs revisions',
          icon: <ClockIcon />
        }
      }
      if (['researcher-submitted'].includes(workflow.status)) {
        return {
          toolTip: 'Candidate has been submitted and is awaiting initial review',
          title: 'In initial review',
          icon: <ClockIcon />
        }
      }

      if (['reviewer-submitted'].includes(workflow.status)) {
        return {
          toolTip: 'Candidate has gone through initial review<br/>and is awaiting your review.',
          title: 'Awaiting review',
          icon: <WarningIcon />
        }
      }

      if (['publisher-submitted'].includes(workflow.status)) {
        return {
          toolTip: 'Candidate has been reviewed and<br/>is waiting to be published.',
          title: 'Ready to publish',
          icon: <WarningIcon />
        }
      }
    } else if(reviewerPermission) {
      if (workflow.status === 'draft' && !workflow.rejectionStatus) {
        return {
          toolTip: 'Candidate is currently in draft.',
          title: 'Draft',
          icon: <ClockIcon />
        }
      }
      if (['draft','researcher-submitted'].includes(workflow.status) && workflow.rejectionStatus) {
        return {
          toolTip: 'Waiting for the original researcher to<br/>address comments from your last revision.',
          title: 'Needs revisions',
          icon: <ClockIcon />
        }
      }
      if (['researcher-submitted'].includes(workflow.status)) {
        return {
          toolTip: 'Candidate has been submitted and<br/> is awaiting your review',
          title: 'Awaiting review',
          icon: <WarningIcon />
        }
      }

      if (['reviewer-submitted'].includes(workflow.status) && workflow.rejectionStatus) {
        return {
          toolTip: 'Your draft was rejected by the publisher.<br/>Please address comments and re-submit for review.',
          title: 'Needs revisions',
          icon: <WarningIcon />
        }
      }

      if (['reviewer-submitted', 'publisher-submitted'].includes(workflow.status)) {
        return {
          toolTip: workflow.status === 'reviewer-submitted'
            ? 'Candidate is awaiting final review.'
            : 'Candidate has been reviewed and<br/>is waiting to be published.',
          title: 'In review',
          icon: <ClockIcon />
        }
      }
    } else if(editPermission) {
      if (workflow.status === 'draft' && !workflow.rejectionStatus) {
        return {
          toolTip: 'Candidate is currently in draft.<br/>Must submit any changes to review.',
          title: 'Draft',
          icon: null
        }
      }
      if (['draft','researcher-submitted'].includes(workflow.status) && workflow.rejectionStatus) {
        return {
          toolTip: 'Your draft was rejected by the reviewer.<br/>Please address comments and re-submit for review.',
          title: 'Needs revisions',
          icon: <WarningIcon />
        }
      }
      if (['researcher-submitted','reviewer-submitted','publisher-submitted'].includes(workflow.status)) {
        return {
          toolTip: workflow.status === 'researcher-submitted'
            ? 'Your draft has been submitted<br/>and is currently awaiting review.'
            : workflow.status === 'reviewer-submitted'
              ? 'Profile is currently awaiting final review.'
              : 'Profile has been fully reviewed.',
          title: 'In review',
          icon: <ClockIcon />
        }
      }
    }

    return {
      toolTip: 'Unknown',
      title: 'Unknown',
      icon: <WarningIcon />
    }
  }, [candidate, workflow, editPermission, publishPermission, reviewerPermission])


  const saveQualificationStatus = async () => {
    if(!editingQualified || saving) return;
    setSaving(true);
    try {
      const update = await feathers.getService('candidates').patch(candidate._id, {
        qualified: qualEdits.qualified
      });
      setEditingQualified(false)
      setCandidate(update);
    } catch(err) {
      console.log(err)
      onError(err)
      setSaving(false)
    }
  }

  const saveWithdrawn = async () => {
    if(!editingWithdrawn || saving) return;
    setSaving(true);
    try {
      const update = await feathers.getService('candidates').patch(candidate._id, {
        withdrawn: withdrawnStatus
      });
      setEditingWithdrawn(false)
      setCandidate(update);
    } catch(err) {
      console.log(err)
      onError(err)
      setSaving(false)
    }
  }

  const saveParty = async () => {
    if(!editingParty || savingParty || !partyInput) return;

    setSavingParty(true);
    try {
      const update = await feathers.getService('candidates').patch(candidate._id, {
        party: partyInput
      });
      setEditingParty(false);
      setCandidate(update);
    } catch(err) {
      console.log(err);
      onError(err);
    } finally {
      setSavingParty(false);
    }
  }

  const updateContact = (index, patch) => {
    setContactEdits(
      contactEdits.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );
  }

  const addContact = () => {
    setContactEdits([...contactEdits, defaultContact]);
  }

  const removeContact = (index) => {
    setContactEdits(contactEdits.filter((_, i) => i !== index));
  }

  const saveContact = async () => {
    if(!editingContact || savingContact) return;

    setSavingContact(true);
    try {
      const contact = (contactEdits || [])
        .map(item => ({
          ...item,
          method: item?.method || 'email',
          value: item?.value?.trim() || '',
          visibility: item?.visibility || 'public',
        }))
        .filter(item => item.value);

      const update = await feathers.getService('candidates').patch(candidate._id, {
        contact
      });
      setEditingContact(false);
      setCandidate(update);
    } catch(err) {
      console.log(err)
      onError(err)
    } finally {
      setSavingContact(false);
    }
  }

  const onChangeFinance = (e) => setNewFinanceInput(e.target.value)
  const saveFinance = async () => {
    const { filerId } = newFinanceInput;
    if(!filerId) {
      cancelFinance()
      return;
    }
    setSavingFinance(true)
    try {
      const updateRes = await feathers.getService('finances').patch(candidate._id, {
        filerId
      })
      setEditingFinance(false)
      setCandidate(updateRes);
    } catch(err) {
      console.log(err)
      onError(err)
    } finally {
      setSavingFinance(false)
    }
  }
  const cancelFinance = () => setEditingFinance(false)

  const refreshFinanceData = async () => {
    if(!candidate?.finance?.filerId || refreshingFinanceData) return;

    setRefreshingFinanceData(true)
    try {
      const updateRes = await feathers.getService('finances').patch(candidate._id, {
        $refresh: true
      })
      setCandidate(updateRes);
    } catch(err) {
      console.log(err)
      onError(err)
    } finally {
      setRefreshingFinanceData(false)
    }
  }

  // Election results functions
  const saveClarityLookup = async () => {
    const { clarityLookup } = newClarityInput;
    if(!clarityLookup) {
      setEditingElectionResults(false)
      return;
    }

    setSavingElectionResults(true)
    try {
      const updateRes = await feathers.getService('election-results').patch(candidate._id, {
        ...clarityLookup
      })
      setEditingElectionResults(false)
      setCandidate(updateRes);
    } catch(err) {
      console.log(err)
      onError(err)
    } finally {
      setSavingElectionResults(false)
    }
  }

  const refreshElectionResults = async () => {
    if(!(candidate?.result?.autoSources?.length > 0) || refreshingElectionResults) return;

    setRefreshingElectionResults(true)
    try {
      const updateRes = await feathers.getService('election-results').patch(candidate._id, {
        $refresh: true
      })
      setCandidate(updateRes);
    } catch(err) {
      console.log(err)
      onError(err)
    } finally {
      setRefreshingElectionResults(false)
    }
  }


  const saveLastChecked = async () => {
    if(!missingInfoChecked || savingLastChecked) return;

    setSavingLastChecked(true)
    try {
      const updateRes = await feathers.getService('candidates').patch(candidate._id, {
        missingInfoChecked: moment.utc(missingInfoChecked).format('YYYY-MM-DDTHH:mm:SSZ')
      })
      setEditingLastChecked(false)
      setCandidate(updateRes);
    } catch(err) {
      console.log(err)
      onError(err)
    } finally {
      setSavingLastChecked(false)
    }
  }

  if(loading) {
    return <Wrapper>
      <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', padding: '36px'}}>
        <LoadingSpinner />
      </div>
    </Wrapper>
  }

  return (
    (<Wrapper>
      {
        race?.runningMates?.enabled &&
        <Section>
          <Typography variant='h3'>Running mate</Typography>
          <div className='seperator' />
          {
            loadingRunningMate
            ?
              <Skeleton variant='rectangular' height={64} />
            :
              runningMateCandidate
              ?
                <RunningMateCard>
                  <CirclePhoto
                    photoPath={runningMateCandidate?.stagingDraft?.photoPathFace || runningMateCandidate?.photoPathFace}
                    hasVariants={
                      (runningMateCandidate?.stagingDraft?.photoPathFace
                        ? runningMateCandidate?.stagingDraft?.hasVariants
                        : runningMateCandidate?.hasVariants) === true
                    }
                    size='small'
                  />
                  <div>
                    <Typography variant='body1'>
                      {runningMateCandidate?.stagingDraft?.name || runningMateCandidate?.name}
                    </Typography>
                    <Link to={`/elections/${election}/races/${runningMateCandidate?.race || race?._id}/candidates/${runningMateCandidate?._id}`}>
                      View candidate details
                    </Link>
                  </div>
                </RunningMateCard>
              :
                <Button
                  onClick={onAddRunningMate}
                  variant='outlined'
                  size='small'
                  startIcon={<AddIcon />}
                  disabled={!editPermission || !onAddRunningMate}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add running mate
                </Button>
          }
        </Section>
      }
      <Section>
        <Typography variant='h3'>Progress</Typography>
        <div className='seperator' />
        <DataField title='Progress'>
          <CandidateProgressBar
            candidate={stagingDraft}
            percentage={progress*100}
            style={{ width: '300px', margin: '6px 0 6px' }}
            backgroundColor='#e0e0e0'
            color='#99D266'
          />
        </DataField>
        <DataField title='Status'>
          <SyncContainer data-tip={formattedWorkflowStatus.toolTip}>
            {formattedWorkflowStatus.icon}
            <Typography variant='body1' style={{ marginTop: '2px' }}>{ formattedWorkflowStatus.title }</Typography>
          </SyncContainer>
        </DataField>
        <DataField title='Priority'>
          <SyncContainer data-tip={'Priority of the candidate is based on expected readership.'}>
            <Typography variant='body1' style={{ marginTop: '2px' }}>{candidate?.priorityLevel ? `${candidate.priorityLevel.slice(0,1).toUpperCase()}${candidate.priorityLevel.slice(1).toLowerCase()}` : 'Not set'}</Typography>
          </SyncContainer>
        </DataField>
      </Section>
      <Section>
        <Typography variant='h3'>Contact information</Typography>
        <div className='seperator' />
        {
          editingContact
          ?
          <FieldInlineEdit
            inputComponent={
              <ContactEditList>
                {(contactEdits || []).length === 0 ? (
                  <Typography variant='body2'>No contact information added yet.</Typography>
                ) : (
                  (contactEdits || []).map((item, index) => (
                    <ContactRow key={`contact-${index}`}>
                      <TextField
                        select
                        size='small'
                        value={item?.method || 'email'}
                        onChange={(e) => updateContact(index, { method: e.target.value })}
                        disabled={savingContact}
                        style={{ width: '110px' }}
                      >
                        {contactMethodOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size='small'
                        value={item?.value || ''}
                        onChange={(e) => updateContact(index, { value: e.target.value })}
                        placeholder='Enter contact value'
                        disabled={savingContact}
                        style={{ flex: 1 }}
                      />
                      <IconButton onClick={() => removeContact(index)} size='small' disabled={savingContact}>
                        <DeleteIcon />
                      </IconButton>
                    </ContactRow>
                  ))
                )}
                <Button
                  onClick={addContact}
                  variant='outlined'
                  size='small'
                  startIcon={<AddIcon />}
                  disabled={savingContact}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add contact
                </Button>
              </ContactEditList>
            }
            loading={savingContact}
            onCancel={() => setEditingContact(false)}
            onSave={saveContact}
            label='Contact information'
          />
          :
          <DataField
            title='Contact'
            editOnClick={false}
            onEdit={
              editPermission
                ? () => setEditingContact(true)
                : null
            }
          >
            {(candidate?.contact || []).length > 0 ? (
              <ContactDisplayList>
                {candidate.contact.map((item, index) => (
                  <ContactDisplayRow key={`contact-display-${index}`}>
                    <Typography variant='body2' className='contact-method'>
                      {getContactMethodLabel(item?.method)}
                    </Typography>
                    <Typography variant='body1' className='contact-value'>
                      {item?.value}
                    </Typography>
                  </ContactDisplayRow>
                ))}
              </ContactDisplayList>
            ) : (
              <Typography variant='body1' style={{ fontSize: '14px' }}>No contact information added.</Typography>
            )}
          </DataField>
        }
      </Section>
      <Section>
        <Typography variant='h3'>Information sources</Typography>
        <div className='seperator' />
        <ReferenceEditor
          priorityLevel={candidate?.priorityLevel}
          references={candidate?.stagingDraft?.references}
          onChangeReferences={() => {}}
          onChangeActiveDraft={(update) => {}}
          disabled={saving}
          activeDraft={candidate?.stagingDraft}
          editingEnabled={false}
          newsEnabled
          scrapedContent={scrapedContent}
          questionnaireEnabled={false}
        />
      </Section>
      <Section>
        <Typography variant='h3'>Ballot display</Typography>
        <div className='seperator' />
        {
          editingParty
          ?
          <FieldInlineEdit
            inputComponent={
              <Select
                onChange={(e) => setPartyInput(e.target.value)}
                name='party'
                value={partyInput}
                disabled={loadingParties || savingParty}
                displayEmpty
                style={{ width: 'calc(100% - 15px)' }}
              >
                <MenuItem value='' disabled>
                  {loadingParties ? 'Loading parties...' : 'Select a party'}
                </MenuItem>
                {partyInput && !partyOptions.some((party) => party.key === partyInput) && (
                  <MenuItem value={partyInput}>
                    {partyInput}
                  </MenuItem>
                )}
                {partyOptions.map((party) => (
                  <MenuItem key={party.key} value={party.key}>
                    {party.name || party.key}
                  </MenuItem>
                ))}
              </Select>
            }
            loading={savingParty}
            onCancel={() => setEditingParty(false)}
            onSave={saveParty}
            label='Party'
          />
          :
          <DataField
            title='Party'
            onEdit={
              editPermission
                ? () => setEditingParty(true)
                : null
            }
          >
            <Typography variant='body1'>
              {partyOptions.find((party) => party.key === candidate?.party)?.name || candidate?.party || '--'}
            </Typography>
          </DataField>
        }
        {
          editingQualified
          ?
          <FieldInlineEdit
              inputComponent={
                <Select
                    onChange={onChangeQualEdits}
                    name='qualified'
                    value={qualEdits.qualified}
                    style={{ width: 'calc(100% - 15px)' }}
                >
                  <MenuItem value='tbd'>To be determined</MenuItem>
                  <MenuItem value='yes'>Qualified</MenuItem>
                  <MenuItem value='no'>Didn't qualify</MenuItem>
                </Select>
              }
              onCancel={() => setEditingQualified(false)}
              onSave={saveQualificationStatus}
              label='Qualified'
          />
          :
          <DataField
              title='Qualified'
              onEdit={
                editPermission
                    ? () => setEditingQualified(true)
                    : null
              }
          >
            <Typography variant='body1' style={{}}>{qualStatus}</Typography>
          </DataField>
        }
        {
          editingWithdrawn
          ?
          <FieldInlineEdit
              inputComponent={
                <Select
                    onChange={(e) => setWithdrawnStatus([true, 'true'].includes(e?.target?.value))}
                    name='withdrawn'
                    value={withdrawnStatus}
                    style={{ width: 'calc(100% - 15px)' }}
                >
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              }
              onCancel={() => setEditingWithdrawn(false)}
              onSave={saveWithdrawn}
              label='Withdrawn'
          />
          :
          <DataField
            title='Withdrawn'
            onEdit={
              editorPermission
                ? () => setEditingWithdrawn(true)
                : researcherPermission
                  ? () => setEditingWithdrawn(true)
                  : null
            }
          >
            <Typography variant='body1' style={{}}>{withdrawn ? 'Yes' : 'No'}</Typography>
          </DataField>
        }
        {
          editingLastChecked &&
          <FieldInlineEdit
            inputComponent={
              <StyledDatePicker
                disableToolbar
                variant="inline"
                format="MM/DD/YYYY"
                value={missingInfoChecked}
                onChange={setMissingInfoChecked}
              />
            }
            disabled={savingLastChecked}
            onCancel={() => setEditingLastChecked(false)}
            onSave={saveLastChecked}
            label='Last checked for platform'
          />
        }
        {
          !editingLastChecked &&
          <DataField
            title='Last checked for platform'
            onEdit={
              editPermission
                ? () => setEditingLastChecked(true)
                : null
            }
          >
            <Typography variant='body1' style={{}}>{moment.utc(candidate?.stagingDraft?.missingInfoChecked || candidate?.createdAt).format('MMMM D')}</Typography>
          </DataField>
        }
        {
          editingRace 
          ?
            <UpdateCandidateRace
              candidate={candidate}
              election={electionData}
              redirectOnFinish={true}
            />
          :
            <DataField 
              title='Race'
              onEdit={
                (editorPermission || researcherPermission)
                  ? () => setEditingRace(true)
                  : null
              }
            >
              <Typography variant='body1' style={{}}>{race?.longName || '--'}</Typography>
            </DataField>
        }
      </Section>
      <Section>
        <Typography variant='h3'>Assignments</Typography>
        <div className='seperator' />
        <DataField title='Researcher'>
          <Researcher user={workflow?.assignments?.researcher} />
        </DataField>
        <DataField title='Reviewer'>
          <Researcher user={workflow?.assignments?.reviewer} />
        </DataField>
        <DataField title='Edited by'>
          {
            editsMadeBy.length > 0
            ? <div style={{ display: 'flex', flexDirection: 'column', gap: '4px'}}>
              {editsMadeBy.map((u, i) => <Typography key={i} variant='body1' style={{}}>{u}</Typography>)}
            </div>
            : <Typography variant='body1' style={{}}>--</Typography>
          }
        </DataField>
      </Section>
      <Section>
        <Typography variant='h3'>Tasks</Typography>
        <div className='seperator' />
        {
          !Array.isArray(tasks) && 
          <Skeleton variant="rectangular" width='100%' height={40} />
        }
        {
          Array.isArray(tasks) && tasks.map((task, i) => (
            <TaskPreviewTile key={i} task={task} />
          ))
        }
      </Section>
      <Section>
        <Typography variant='h3'>Campaign finance</Typography>
        <div className='seperator' />
        {
          editingFinance
          ?
          <FieldInlineEdit
              inputComponent={
                <CFSelectInput
                    onChange={onChangeFinance}
                    value={newFinanceInput}
                    candidateId={candidateId}
                    style={{ width: 'calc(100% - 15px)' }}
                />
              }
              loading={savingFinance}
              style={{ width: 'calc(33% - 15px)', marginBottom: '20px'}}
              onCancel={cancelFinance}
              onSave={saveFinance}
              label='Finance Filer ID'
          />
          :
          <DataField
              title='Finance Filer ID'
              style={{marginBottom: '20px'}}
              onEdit={
                editPermission
                    ? () => setEditingFinance(true)
                    : null
              }
          >
            {
              candidate?.finance?.filerId
                  ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <GreenCheck data-tip='Candidate has been linked to campaign finance disclosure reports.'/>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', flex: 1, marginLeft: '8px' }}>
                          <Typography variant='body1' style={{ fontSize: '14px' }}>{candidate?.finance?.filerId}</Typography>
                          <Typography variant='subtitle2' style={{ fontSize: '12px' }}>
                            Last updated {moment.utc(candidate?.finance?.updatedAt).format('MMM DD, YYYY')} · {` `}
                            {
                              refreshingFinanceData
                                  ? `Refreshing...`
                                  : <u style={{ cursor: 'pointer', fontSize: '12px' }} onClick={() => refreshFinanceData()}>Refresh</u>
                            }
                          </Typography>
                        </div>
                      </div>
                  ) : (
                      <Typography variant='body1' style={{ fontSize: '14px' }}>No campaign finance record linked.</Typography>
                  )
            }
          </DataField>
        }
      </Section>
      <Section>
        <Typography variant='h3'>Election results</Typography>
        <div className='seperator' />
          { editingElectionResults
              ?
              <FieldInlineEdit
                  inputComponent={
                    <ElectionResultsSelectInput
                        onChange={(e) => setNewClarityInput(e.target.value)}
                        value={newClarityInput}
                        candidateId={candidateId}
                        style={{ width: 'calc(100%' }}
                    />
                  }
                  loading={savingElectionResults}
                  style={{ width: 'calc(100% - 15px)'}}
                  onCancel={() => setEditingElectionResults(false)}
                  onSave={saveClarityLookup}
                  label='Election Results Link'
              />
              :
              <DataField
                  title='Election Results Link'
                  onEdit={
                    editPermission
                        ? () => setEditingElectionResults(true)
                        : null
                  }
              >
                {
                  candidate?.result?.autoSources?.length > 0
                      ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <GreenCheck data-tip='Candidate has been linked to Clarity election results system.'/>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', flex: 1, marginLeft: '8px' }}>
                              <Typography variant='body1' style={{ fontWeight: 'bold', fontSize: '14px' }}>Won {candidate.result.autoSources[0].totalVotes} votes</Typography>
                              <Typography variant='subtitle2' style={{ fontSize: '12px' }}>
                                Last updated {moment.utc(candidate.result.autoSources[0].updatedAt).fromNow()} · {` `}
                                {
                                  refreshingElectionResults
                                      ? `Refreshing...`
                                      : <u style={{ cursor: 'pointer', fontSize: '12px' }} onClick={() => refreshElectionResults()}>Refresh</u>
                                }
                              </Typography>
                            </div>
                          </div>
                      ) : (
                          <Typography variant='body1' style={{ fontSize: '14px' }}>No election results linked.</Typography>
                      )
                }
              </DataField>
          }
          <ManualVoteTally
            candidateId={candidateId}
            resultManual={candidate?.result?.manual}
            onSave={(res) => setCandidate(res)}
          />
      </Section>
      <ProfilerEvaluationsSection candidate={candidate} feathers={feathers} />
    </Wrapper>)
  );
}


const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 48px;
`

const SyncContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  svg {
    width: 34px;
    margin-right: 6px;
  }
`

const ContactDisplayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ContactDisplayRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .contact-method {
    color: #666666;
    font-size: 12px;
    line-height: 1;
  }

  .contact-value {
    font-size: 14px;
    overflow-wrap: anywhere;
  }
`

const ContactEditList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const RunningMateCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  a {
    display: inline-block;
    margin-top: 4px;
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-left: 16px;
  gap: 16px;

  h3 {
    margin-left: -16px;
    margin-bottom: -8px;
    font-size: 16px;
  }

  .seperator {
    height: 1px;
    width: 100%;
    background-color: #DDDDDD;
    margin: 0px 0 0px -16px;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiInputBase-input {
    padding: 2px 8px;
  }

	.MuiInputLabel-outlined {
		margin-top: -10px;
	}
`;


export { CandidateDataFieldEditor };